import React, { useState, useMemo } from 'react';
import { Search, Plus, Edit, Trash2, User, AlertTriangle, X, Save } from 'lucide-react';
import { formatDate, calculateDaysUntilExpiration, createStudent, DISABILITY_CATEGORIES } from '../utils/dataModels';
import { useStudents } from '../hooks/useStudents';
import ScreenReaderContent from './ScreenReaderContent';
import Tooltip from './Tooltip';

const StudentList = ({ students, onNavigate, announce }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('lastName');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showAddForm, setShowAddForm] = useState(false);
  const { addStudent, deleteStudent } = useStudents();

  const filteredAndSortedStudents = useMemo(() => {
    let filtered = students.filter(student => {
      const fullName = `${student.firstName} ${student.lastName}`.toLowerCase();
      const searchLower = searchTerm.toLowerCase();
      return fullName.includes(searchLower) || 
             student.grade.toLowerCase().includes(searchLower) ||
             student.disabilityCategory.toLowerCase().includes(searchLower);
    });

    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'firstName':
          aValue = a.firstName.toLowerCase();
          bValue = b.firstName.toLowerCase();
          break;
        case 'lastName':
          aValue = a.lastName.toLowerCase();
          bValue = b.lastName.toLowerCase();
          break;
        case 'grade':
          aValue = a.grade;
          bValue = b.grade;
          break;
        case 'iepEndDate':
          aValue = new Date(a.iepEndDate);
          bValue = new Date(b.iepEndDate);
          break;
        default:
          aValue = a.lastName.toLowerCase();
          bValue = b.lastName.toLowerCase();
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [students, searchTerm, sortBy, sortOrder]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    
    announce(`Sorted by ${field} ${sortOrder === 'asc' ? 'descending' : 'ascending'}`);
  };

  const handleDeleteStudent = async (studentId, studentName) => {
    if (window.confirm(`Are you sure you want to delete ${studentName}? This will also delete all associated goals and cannot be undone.`)) {
      const success = deleteStudent(studentId);
      if (success) {
        announce(`${studentName} has been deleted`);
      } else {
        announce('Error deleting student', 'assertive');
      }
    }
  };

  const StudentRow = ({ student }) => {
    const daysUntil = calculateDaysUntilExpiration(student.iepEndDate);
    const isExpiringSoon = daysUntil <= 90;
    
    return (
      <tr 
        className="hover:bg-gray-50 transition-colors cursor-pointer"
        onClick={() => onNavigate('students', { studentId: student.id })}
      >
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex items-center">
            <div className="flex-shrink-0 h-10 w-10">
              <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                <User size={20} className="text-gray-600" />
              </div>
            </div>
            <div className="ml-4">
              <div className="text-sm font-medium text-gray-900">
                {student.firstName} {student.lastName}
              </div>
              <div className="text-sm text-gray-900">
                {student.grade} Grade
              </div>
            </div>
          </div>
        </td>
        
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {student.disabilityCategory}
        </td>
        
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {formatDate(student.iepStartDate)}
        </td>
        
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex items-center">
            <span className="text-sm text-gray-900">
              {formatDate(student.iepEndDate)}
            </span>
            {isExpiringSoon && (
              <div className="ml-2 flex items-center">
                <Tooltip content={`IEP expires in ${daysUntil} days - ${daysUntil <= 30 ? 'Urgent action needed' : daysUntil <= 60 ? 'Review soon' : 'Upcoming expiration'}`}>
                  <AlertTriangle 
                    size={16} 
                    className={`${
                      daysUntil <= 30 ? 'text-red-500' : 
                      daysUntil <= 60 ? 'text-yellow-500' : 'text-orange-500'
                    }`}
                  />
                </Tooltip>
                <ScreenReaderContent>
                  IEP expires in {daysUntil} days
                </ScreenReaderContent>
              </div>
            )}
          </div>
        </td>
        
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {student.caseManager}
        </td>
        
        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
          <div className="flex items-center justify-end space-x-2">
            <button
              onClick={() => onNavigate('students', { studentId: student.id })}
              className="text-primary hover:text-blue-900 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary p-1 rounded"
              aria-label={`View details for ${student.firstName} ${student.lastName}`}
            >
              <User size={16} />
            </button>
            <button
              onClick={() => onNavigate('students', { studentId: student.id, action: 'edit' })}
              className="text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-500 p-1 rounded"
              aria-label={`Edit ${student.firstName} ${student.lastName}`}
            >
              <Edit size={16} />
            </button>
            <button
              onClick={() => handleDeleteStudent(student.id, `${student.firstName} ${student.lastName}`)}
              className="text-red-600 hover:text-red-900 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-red-500 p-1 rounded"
              aria-label={`Delete ${student.firstName} ${student.lastName}`}
            >
              <Trash2 size={16} />
            </button>
          </div>
        </td>
      </tr>
    );
  };

  const SortButton = ({ field, children }) => (
    <button
      onClick={() => handleSort(field)}
      className="flex items-center space-x-1 font-medium text-gray-900 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary"
      aria-label={`Sort by ${children} ${sortBy === field ? (sortOrder === 'asc' ? 'descending' : 'ascending') : 'ascending'}`}
    >
      <span>{children}</span>
      {sortBy === field && (
        <span className="text-primary">
          {sortOrder === 'asc' ? '↑' : '↓'}
        </span>
      )}
    </button>
  );

  // Add Student Form State
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    grade: '',
    disabilityCategory: '',
    iepStartDate: '',
    iepEndDate: '',
    caseManager: '',
    parentContact: {
      name: '',
      email: '',
      phone: ''
    }
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('parent')) {
      const field = name.replace('parent', '').charAt(0).toLowerCase() + name.replace('parent', '').slice(1);
      setFormData(prev => ({
        ...prev,
        parentContact: {
          ...prev.parentContact,
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newStudent = createStudent(formData);
    const result = addStudent(newStudent);
    
    if (result) {
      announce(`${formData.firstName} ${formData.lastName} has been added`);
      setShowAddForm(false);
      setFormData({
        firstName: '',
        lastName: '',
        grade: '',
        disabilityCategory: '',
        iepStartDate: '',
        iepEndDate: '',
        caseManager: '',
        parentContact: {
          name: '',
          email: '',
          phone: ''
        }
      });
    } else {
      announce('Error adding student', 'assertive');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Students</h1>
          <p className="text-gray-900 mt-1">
            Manage your student roster ({filteredAndSortedStudents.length} students)
          </p>
        </div>
        
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
        >
          {showAddForm ? (
            <>
              <X size={16} className="mr-2" />
              Cancel
            </>
          ) : (
            <>
              <Plus size={16} className="mr-2" />
              Add Student
            </>
          )}
        </button>
      </div>

      {/* Add Student Form */}
      {showAddForm && (
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Add New Student</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                  First Name *
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  required
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                />
              </div>
              
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                  Last Name *
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  required
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                />
              </div>
              
              <div>
                <label htmlFor="grade" className="block text-sm font-medium text-gray-700">
                  Grade *
                </label>
                <select
                  id="grade"
                  name="grade"
                  required
                  value={formData.grade}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                >
                  <option value="">Select grade</option>
                  {['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'].map(grade => (
                    <option key={grade} value={grade}>{grade}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label htmlFor="disabilityCategory" className="block text-sm font-medium text-gray-700">
                  Disability Category *
                </label>
                <select
                  id="disabilityCategory"
                  name="disabilityCategory"
                  required
                  value={formData.disabilityCategory}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                >
                  <option value="">Select category</option>
                  {DISABILITY_CATEGORIES.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label htmlFor="iepStartDate" className="block text-sm font-medium text-gray-700">
                  IEP Start Date *
                </label>
                <input
                  type="date"
                  id="iepStartDate"
                  name="iepStartDate"
                  required
                  value={formData.iepStartDate}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                />
              </div>
              
              <div>
                <label htmlFor="iepEndDate" className="block text-sm font-medium text-gray-700">
                  IEP End Date *
                </label>
                <input
                  type="date"
                  id="iepEndDate"
                  name="iepEndDate"
                  required
                  value={formData.iepEndDate}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                />
              </div>
              
              <div className="md:col-span-2">
                <label htmlFor="caseManager" className="block text-sm font-medium text-gray-700">
                  Case Manager *
                </label>
                <input
                  type="text"
                  id="caseManager"
                  name="caseManager"
                  required
                  value={formData.caseManager}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                />
              </div>
            </div>
            
            <div className="border-t pt-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Parent/Guardian Contact</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="parentName" className="block text-sm font-medium text-gray-700">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="parentName"
                    name="parentName"
                    required
                    value={formData.parentContact.name}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  />
                </div>
                
                <div>
                  <label htmlFor="parentEmail" className="block text-sm font-medium text-gray-700">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="parentEmail"
                    name="parentEmail"
                    required
                    value={formData.parentContact.email}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  />
                </div>
                
                <div>
                  <label htmlFor="parentPhone" className="block text-sm font-medium text-gray-700">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    id="parentPhone"
                    name="parentPhone"
                    required
                    value={formData.parentContact.phone}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  />
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                <Save size={16} className="mr-2" />
                Add Student
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white p-4 rounded-lg border">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label htmlFor="search-students" className="sr-only">
              Search students
            </label>
            <div className="relative">
              <input
                id="search-students"
                type="text"
                placeholder="Search students..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Students List */}
      {filteredAndSortedStudents.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <div className="text-gray-500">
            {searchTerm ? (
              <>
                <Search size={48} className="mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-medium mb-2">No students found</h3>
                <p>Try adjusting your search terms</p>
              </>
            ) : (
              <>
                <User size={48} className="mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-medium mb-2">No students yet</h3>
                <p>Add your first student to get started</p>
                <button
                  onClick={() => setShowAddForm(true)}
                  className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-primary bg-primary/10 hover:bg-primary/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  <Plus size={16} className="mr-2" />
                  Add Student
                </button>
              </>
            )}
          </div>
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="hidden md:block bg-white shadow overflow-hidden rounded-lg">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      <SortButton field="lastName">Student</SortButton>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Disability Category
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      IEP Start Date
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      <SortButton field="iepEndDate">IEP End Date</SortButton>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Case Manager
                    </th>
                    <th scope="col" className="relative px-6 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredAndSortedStudents.map((student) => (
                    <StudentRow key={student.id} student={student} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-6 px-2">
            {filteredAndSortedStudents.map((student) => {
              const daysUntil = calculateDaysUntilExpiration(student.iepEndDate);
              const isExpiringSoon = daysUntil <= 90;
              
              return (
                <div 
                  key={student.id} 
                  className="bg-white shadow rounded-lg p-6 relative cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => onNavigate('students', { studentId: student.id })}
                >
                  {isExpiringSoon && (
                    <div className="absolute" style={{ top: '1rem', right: '1rem' }}>
                      <Tooltip content={`IEP expires in ${daysUntil} days - ${daysUntil <= 30 ? 'Urgent action needed' : daysUntil <= 60 ? 'Review soon' : 'Upcoming expiration'}`}>
                        <AlertTriangle 
                          size={20} 
                          className={`${
                            daysUntil <= 30 ? 'text-red-500' : 
                            daysUntil <= 60 ? 'text-yellow-500' : 'text-orange-500'
                          } cursor-help`}
                        />
                      </Tooltip>
                    </div>
                  )}
                  <div className="text-center mb-4">
                    <div className="flex justify-center mb-2">
                      <User size={24} className="text-gray-900" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {student.firstName} {student.lastName}
                    </h3>
                    <p className="text-sm text-gray-900 -mt-1">{student.grade} Grade</p>
                  </div>
                  
                  <div className="space-y-3 text-sm">
                    <div className="text-center">
                      <div className="text-gray-900 text-xs uppercase tracking-wider mb-1 font-semibold">Disability</div>
                      <div className="text-gray-900 font-medium">{student.disabilityCategory}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-gray-900 text-xs uppercase tracking-wider mb-1 font-semibold">IEP End Date</div>
                      <div className="text-gray-900 font-medium">
                        {formatDate(student.iepEndDate)}
                        {isExpiringSoon && (
                          <div className="text-xs mt-1 text-red-600 font-bold">Expires in {daysUntil} days</div>
                        )}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-gray-900 text-xs uppercase tracking-wider mb-1 font-semibold">Case Manager</div>
                      <div className="text-gray-900 font-medium">{student.caseManager}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default StudentList;